// Synthesize SPA-local `FeedEntry[]` from a batch of `WireEvent`s plus
// the BattleState **before** the batch is applied. Feed entries are a
// pure SPA concept now — the v2 wire ships only events; `EventLog`
// renders the entries this module synthesizes for plays, deaths,
// damage, phase ticks, etc.
//
// Reads from the *pre-batch* `BattleState` so we can look up entity
// metadata (name, def_id) for cards whose state will change inside the
// batch — e.g. a creature that dies mid-batch is still in `state.entities`
// at the point the death event fires.

import type { EntityRecord, Phase, PlayerId, WireEvent } from "../api/types";
import type { BattleState } from "./battleReducer";
import type { FeedEntry, FeedKind, FeedPart } from "./feed";

interface DerivedContext {
  /** Pre-batch state — the entity metadata is read off this. */
  prev: BattleState;
  /** The viewer's seat — used to decide "you" vs "opponent" for banner
   *  phrasing. */
  viewer: PlayerId;
}

/** Produce the feed entries (in display order) for one batch of wire
 *  events. */
export function feedFromEvents(
  events: WireEvent[],
  ctx: DerivedContext,
): FeedEntry[] {
  const out: FeedEntry[] = [];
  // Track entities revealed inside this batch so subsequent events in
  // the same batch can resolve their names without going back to a
  // mutable state. Seeded from the pre-batch entities.
  const known = new Map<number, EntityRecord>(ctx.prev.entities);
  // Same idea for `defs`: the Reveal event carries the EntityDef the
  // first time the viewer sees a new card. Without folding those into
  // the lookup map here, a "played" / "drew" entry for an entity the
  // opp just revealed renders the raw `def_id` ("antaran_recruit")
  // instead of the human name ("Antaran Recruit"), because the
  // pre-batch `defs` doesn't have the def yet.
  const defs = new Map(ctx.prev.defs);
  for (const ev of events) {
    if (ev.k === "reveal") {
      known.set(ev.eid, ev.record);
      if (ev.def && !defs.has(ev.def.def_id)) defs.set(ev.def.def_id, ev.def);
    }
  }
  const batchCtx: DerivedContext = { ...ctx, prev: { ...ctx.prev, defs } };

  for (const ev of events) {
    const entry = entryFor(ev, batchCtx, known);
    if (entry) out.push(entry);
  }
  return out;
}

function entryFor(
  ev: WireEvent,
  ctx: DerivedContext,
  known: Map<number, EntityRecord>,
): FeedEntry | null {
  switch (ev.k) {
    case "turn": {
      // The wire fires a Turn event on EITHER a turn-number change or
      // an active-player change, so the same turn number shows up
      // twice in a multi-turn batch (once for our half, once for the
      // opp's half) and reads as a duplicate banner. Tag the banner
      // with who's about to play so the two halves stay distinct.
      const yours = ev.active === ctx.viewer;
      const label = yours
        ? `── Your Turn ${ev.to} ──`
        : `── Opp's Turn ${ev.to} ──`;
      return banner("turn_banner", label);
    }
    case "phase":
      return banner("phase_banner", phaseLabel(ev.to));
    case "move": {
      const card = cardPart(ev.eid, known, ctx);
      if (!card) return null;
      const controller = known.get(ev.eid)?.controller ?? null;
      const playerPart: FeedPart | null =
        controller != null ? { type: "player", id: controller } : null;
      // The cast pipeline emits two zone events per cast: a
      // `Move(hand → stack)` at the start of the cast (so opponents
      // see the spell going up before any counter-trap springs) and
      // `Move(stack → <destination>)` when the cast resolves. The
      // start-of-cast move is the canonical "played" beat — log it
      // there and skip the resolve half so a single cast produces a
      // single feed entry across two animation frames. The matching
      // `Fizzled` wire event further down renders the explicit
      // "X fizzled" line when the cast was countered.
      const playLike = ev.from === "hand" && ev.to === "stack";
      if (playLike) {
        const k: FeedKind = card.type === "spell" ? "cast" : "played";
        const parts: FeedPart[] = playerPart
          ? [
              playerPart,
              text(k === "cast" ? " cast " : " played "),
              card.part,
            ]
          : [card.part];
        return { kind: k, parts };
      }
      if (ev.from === "deck" && ev.to === "hand") {
        const parts: FeedPart[] = playerPart
          ? [playerPart, text(" drew "), card.part]
          : [card.part, text(" drawn")];
        return { kind: "drew", parts };
      }
      if (ev.from === "battle" && ev.to === "graveyard") {
        return null;
      }
      if (ev.from === "battle" && ev.to === "exile") {
        return { kind: "exiled", parts: [card.part, text(" exiled")] };
      }
      if (ev.from === "hand" && ev.to === "graveyard") {
        return { kind: "discarded", parts: [card.part, text(" discarded")] };
      }
      // `stack → graveyard` is the spell-resolution move (and the
      // engine fizzle path) — the wire already shipped the "cast"
      // entry on the partner `hand → stack` move, and the explicit
      // `Fizzled` wire event below handles the "X fizzled" line.
      // Just swallow this Move so we don't double-log.
      if (ev.from === "stack" && ev.to === "graveyard") {
        return null;
      }
      if (ev.from === "deck" && ev.to === "graveyard") {
        return { kind: "milled", parts: [card.part, text(" milled")] };
      }
      if (ev.to === "deck") {
        return { kind: "returned_to_deck", parts: [card.part, text(" returned to deck")] };
      }
      return null;
    }
    case "die": {
      const card = cardPart(ev.eid, known, ctx);
      if (!card) return null;
      return { kind: "died", parts: [card.part, text(" died")] };
    }
    case "trap_sprung": {
      const card = cardPart(ev.eid, known, ctx);
      if (!card) return null;
      return { kind: "trap_sprung", parts: [card.part, text(" sprang")] };
    }
    case "fizzled": {
      const card = cardPart(ev.eid, known, ctx);
      if (!card) return null;
      return { kind: "fizzled", parts: [card.part, text(" fizzled")] };
    }
    case "damage": {
      // Creature-on-creature damage in combat fills the feed with
      // every pair-wise "X dealt N damage to Y" line — for a 3-way
      // block that's 6 lines of swing-and-trade noise the user
      // already sees on the board via the −N marker and the death
      // entries. Skip them entirely; player damage stays so the
      // viewer can see opponent life ticking down. Source is read
      // off the wire when present ("Boiler Golem dealt 5 to Opp"),
      // otherwise the passive form for sourceless damage (fatigue,
      // future scripted effects).
      if (ev.target.kind !== "player") return null;
      const source = ev.source != null ? cardPart(ev.source, known, ctx) : null;
      const seat = ev.target.seat;
      const target: FeedPart = { type: "player", id: seat };
      if (source) {
        return {
          kind: "player_damaged",
          parts: [
            source.part,
            text(` dealt ${ev.amount} damage to `),
            target,
          ],
        };
      }
      return {
        kind: "player_damaged",
        parts: [target, text(` took ${ev.amount} damage`)],
      };
    }
    case "attackers_declared": {
      if (ev.attackers.length === 0) return null;
      const parts: FeedPart[] = [];
      ev.attackers.forEach((eid, i) => {
        if (i > 0) parts.push(text(", "));
        const c = cardPart(eid, known, ctx);
        if (c) parts.push(c.part);
      });
      parts.push(text(" attacked"));
      return { kind: "attacked", parts };
    }
    case "block": {
      const a = cardPart(ev.attacker, known, ctx);
      const b = cardPart(ev.blocker, known, ctx);
      if (!a || !b) return null;
      return {
        kind: "blocked",
        parts: [b.part, text(" blocked "), a.part],
      };
    }
    case "game_over": {
      const result = ev.result;
      if (result.kind === "winner") {
        return {
          kind: "result",
          parts: [
            { type: "player", id: result.player } as FeedPart,
            text(" won"),
          ],
        };
      }
      return { kind: "result", parts: [text("Draw")] };
    }
    case "attr": {
      // Most attr patches (stat buffs, face-down flips, damage) are
      // silent. A control change is the exception — surface it as its
      // own "seized" line so the player sees who took the card.
      const to = ev.fields.controller;
      if (to == null) return null;
      const card = cardPart(ev.eid, known, ctx);
      if (!card) return null;
      return {
        kind: "seized",
        parts: [
          { type: "player", id: to } as FeedPart,
          text(" seized "),
          card.part,
        ],
      };
    }
    case "notice": {
      // System line, e.g. the per-player card cap was reached. The
      // server ships the predicate text; we front it with the affected
      // player so it reads "You reached …" / "Opponent reached …".
      return {
        kind: "notice",
        parts: [{ type: "player", id: ev.seat } as FeedPart, text(` ${ev.text}`)],
      };
    }
    // Scalar / patch / structural events that don't surface as feed lines.
    case "reveal":
    case "keyword_add":
    case "keyword_remove":
    case "counter":
    case "life":
    case "mana":
    case "max_mana":
    case "hand_size":
    case "deck_size":
    case "graveyard_size":
    case "exile_size":
    case "castable":
    case "pending_changed":
      return null;
  }
}

function banner(kind: FeedKind, text_: string): FeedEntry {
  return { kind, parts: [text(text_)] };
}

function text(s: string): FeedPart {
  return { type: "text", text: s };
}

function cardPart(
  eid: number,
  known: Map<number, EntityRecord>,
  ctx: DerivedContext,
): { part: FeedPart; type: string } | null {
  const rec = known.get(eid);
  if (!rec) return null;
  const def_id = rec.def_id ?? "";
  const def = def_id ? ctx.prev.defs.get(def_id) : null;
  const name = def?.name ?? (def_id || "Trap");
  const type = def?.type ?? "creature";
  return {
    part: {
      type: "card",
      eid,
      def_id,
      name,
    } as FeedPart,
    type,
  };
}

function phaseLabel(phase: Phase): string {
  switch (phase) {
    case "main":
      return "── Main ──";
    case "combat_start":
      return "── Combat ──";
    case "combat_declare_attackers":
      return "── Declare Attackers ──";
    case "combat_declare_blockers":
      return "── Declare Blockers ──";
    case "combat_damage":
      return "── Combat Damage ──";
    case "turn_start":
      return "── Turn Start ──";
    case "draw":
      return "── Draw ──";
    case "untap":
      return "── Untap ──";
    case "turn_end":
      return "── End of Turn ──";
    case "game_over":
      return "── Game Over ──";
  }
}
